import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { TopicOrderingField, PostOrderingField } from "../types/api/forum";
import {
  fetchTopics,
  deleteTopics,
  setTopicsVisibility,
  fetchPosts,
  deletePosts,
} from "../services/forum";

// Hooks для топиков форума

// Постраничный запрос топиков
export const useTopicsQuery = (
  search: string = "",
  page: number,
  limit: number,
  catalogId?: string,
  categoryId?: string,
  createdFrom?: string,
  createdTo?: string,
  userId?: string,
  ordering?: TopicOrderingField
) => {
  return useQuery({
    refetchOnWindowFocus: false,
    queryKey: [
      "topics",
      search,
      page,
      limit,
      catalogId,
      categoryId,
      createdFrom,
      createdTo,
      userId,
      ordering,
    ],
    queryFn: () =>
      fetchTopics({
        search: search || undefined,
        limit,
        offset: (page - 1) * limit,
        catalog_id: catalogId,
        category_id: categoryId,
        created_from: createdFrom,
        created_to: createdTo,
        user_id: userId,
        ordering,
      }),
    retry: 0,
  });
};

// Бесконечная прокрутка топиков
export const useTopicsInfiniteQuery = (
  search: string = "",
  limit: number,
  catalogId?: string,
  categoryId?: string,
  createdFrom?: string,
  createdTo?: string,
  userId?: string,
  ordering?: TopicOrderingField,
  options?: {
    enabled?: boolean;
  }
) => {
  return useInfiniteQuery({
    queryKey: [
      "topics-infinite",
      search,
      limit,
      catalogId,
      categoryId,
      createdFrom,
      createdTo,
      userId,
      ordering,
    ],
    queryFn: ({ pageParam = { limit: limit, offset: 0 } }) =>
      fetchTopics({
        search: search || undefined,
        limit: pageParam.limit,
        offset: pageParam.offset,
        catalog_id: catalogId,
        category_id: categoryId,
        created_from: createdFrom,
        created_to: createdTo,
        user_id: userId,
        ordering,
      }),
    initialPageParam: { limit: limit, offset: 0 },
    getNextPageParam: (lastPage, allPages) => {
      const totalLoaded = allPages.reduce(
        (acc, page) => acc + page.list.length,
        0
      );
      const hasMore = lastPage.list.length === limit;
      return hasMore ? { limit: limit, offset: totalLoaded } : undefined;
    },
    retry: 0,
    enabled: options?.enabled ?? true,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
};

// Мутация удаления топиков
export const useDeleteTopicsMutation = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteTopics,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["topics"] });
      queryClient.invalidateQueries({ queryKey: ["topics-infinite"] });
      onSuccess?.();
    },
  });
};

// Мутация скрытия и показа топиков
export const useSetTopicsVisibilityMutation = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: setTopicsVisibility,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["topics"] });
      queryClient.invalidateQueries({ queryKey: ["topics-infinite"] });
      onSuccess?.();
    },
  });
};

// Hooks для постов форума

// Постраничный запрос постов
export const usePostsQuery = (
  search: string = "",
  page: number,
  limit: number,
  topicId?: string,
  createdFrom?: string,
  createdTo?: string,
  ordering?: PostOrderingField
) => {
  return useQuery({
    refetchOnWindowFocus: false,
    queryKey: [
      "posts",
      search,
      page,
      limit,
      topicId,
      createdFrom,
      createdTo,
      ordering,
    ],
    queryFn: () =>
      fetchPosts({
        search: search || undefined,
        limit,
        offset: (page - 1) * limit,
        topic_id: topicId,
        created_from: createdFrom,
        created_to: createdTo,
        ordering,
      }),
    retry: 0,
  });
};

// Бесконечная прокрутка постов
export const usePostsInfiniteQuery = (
  search: string = "",
  limit: number,
  topicId?: string,
  createdFrom?: string,
  createdTo?: string,
  ordering?: PostOrderingField,
  options?: {
    enabled?: boolean;
  }
) => {
  return useInfiniteQuery({
    queryKey: [
      "posts-infinite",
      search,
      limit,
      topicId,
      createdFrom,
      createdTo,
      ordering,
    ],
    queryFn: ({ pageParam = { limit: limit, offset: 0 } }) =>
      fetchPosts({
        search: search || undefined,
        limit: pageParam.limit,
        offset: pageParam.offset,
        topic_id: topicId,
        created_from: createdFrom,
        created_to: createdTo,
        ordering,
      }),
    initialPageParam: { limit: limit, offset: 0 },
    getNextPageParam: (lastPage, allPages) => {
      const totalLoaded = allPages.reduce(
        (acc, page) => acc + page.list.length,
        0
      );
      const hasMore = lastPage.list.length === limit;
      return hasMore ? { limit: limit, offset: totalLoaded } : undefined;
    },
    retry: 0,
    enabled: options?.enabled ?? true,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
};

// Мутация удаления постов
export const useDeletePostsMutation = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deletePosts,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["posts"] });
      queryClient.invalidateQueries({ queryKey: ["posts-infinite"] });
      onSuccess?.();
    },
  });
};
